'use client';

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@eliadmin/core/src/components/ui/alert-dialog';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Modal } from '@eliadmin/core/src/components/ui/modal';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { deleteVehicle, type Vehicle } from '../../api-custom/vehicles-api';
import { VehicleForm } from './vehicle-form';

const FUEL_TYPE_LABELS: Record<string, string> = {
  petrol: 'Benzín',
  diesel: 'Nafta',
  electric: 'Elektrina',
  hybrid: 'Hybrid',
  lpg: 'LPG',
};

export function VehicleCard({ vehicle }: { vehicle: Vehicle }) {
  const { t } = useTranslations();
  const authToken = useCustomerStore(s => s.me!.authToken);
  const locale = useLocaleStore(s => s.currentLocale);
  const queryClient = useQueryClient();

  const [isEditOpen, setIsEditOpen] = useState(false);

  // Názov poisťovne príde z backendu v `insurer_name`, `insurance_company_name` je len fallback.
  const insuranceCompany = vehicle.insurer_name || vehicle.insurance_company_name;

  const { mutate: remove, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteVehicle({ locale, authToken, id: vehicle.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast({ title: 'Vozidlo bolo vymazané.', variant: 'success' });
    },
    onError: () => {
      toast({
        title: 'Vymazanie vozidla zlyhalo. Skúste to prosím znova.',
        variant: 'destructive',
      });
    },
  });

  return (
    <div className="flex w-full flex-col gap-4 rounded-2xl border border-border bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-base font-semibold">{vehicle.vehicle_plate}</span>
          <Badge variant={vehicle.is_active ? 'default' : 'secondary'}>
            {vehicle.is_active ? t('text2085') : t('text4055')}
          </Badge>
          <Badge variant={vehicle.vehicle_insurance ? 'accent' : 'secondary'}>
            {vehicle.vehicle_insurance ? t('text4056') : t('text4057')}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{vehicle.vehicle_type}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>VIN: {vehicle.vin_number || '—'}</span>
          <span>Palivo: {FUEL_TYPE_LABELS[vehicle.fuel_type] ?? vehicle.fuel_type}</span>
          {insuranceCompany && <span>Poisťovacia spoločnosť: {insuranceCompany}</span>}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Modal
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          title={t('text4059')}
          showCustomClose
          trigger={
            <Button type="button" variant="outline" className="rounded-[6px]">
              <FiEdit2 size={16} /> {t('text754')}
            </Button>
          }
        >
          <VehicleForm vehicle={vehicle} onSuccess={() => setIsEditOpen(false)} />
        </Modal>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button type="button" variant="destructive" className="rounded-[6px]">
              <FiTrash2 size={16} /> {t('text1611')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('text4060')}</AlertDialogTitle>
              <AlertDialogDescription>
                Vozidlo {vehicle.vehicle_plate} bude natrvalo odstránené z vášho vozového parku.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('text377')}</AlertDialogCancel>
              <AlertDialogAction disabled={isDeleting} onClick={() => remove()}>
                {t('text1611')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
